"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.computeUnreadDivider = computeUnreadDivider;
/**
 * Derives where the unread divider sits and whether there are unread messages
 * above the loaded window, from the snapshotted boundary (the last-read message
 * id). Shared by web and mobile chat panels.
 *
 * messages[0] is the newest (both the web list and the mobile FlatList are
 * reversed/inverted), so the boundary sits at boundaryIndex and the first
 * unread is the message one newer than it.
 * - boundary loaded and not the newest -> firstUnreadIndex >= 0 (render divider)
 * - boundary is the newest loaded message -> nothing unread above
 * - boundary not loaded -> divider can't render yet; there are unread above only
 *   if more (older) pages remain to load (hasNext)
 *
 * `boundaryMessageId` distinguishes three cases:
 * - `undefined` -> no snapshot / nothing unread -> no divider.
 * - `""` (empty) -> never-opened chat: unread with no read pointer. Everything
 *   is unread, so the divider goes before the first real message, after the
 *   leading info/system messages (which sit at the oldest end of the list).
 * - non-empty -> a known last-read message id (normal case).
 */
function computeUnreadDivider(messages, boundaryMessageId, hasNext) {
    if (boundaryMessageId === undefined) {
        return { firstUnreadIndex: -1, hasUnreadAbove: false };
    }
    if (boundaryMessageId === "") {
        // Never-opened chat. If older messages aren't loaded yet the true start
        // isn't in the window, so fall back to the chip.
        if (hasNext) {
            return { firstUnreadIndex: -1, hasUnreadAbove: true };
        }
        // Messages are newest-first; scan from the oldest end for the first real
        // (non-info) message — the divider renders just above it.
        for (let i = messages.length - 1; i >= 0; i--) {
            if (!messages[i].isInfoMessage()) {
                return { firstUnreadIndex: i, hasUnreadAbove: true };
            }
        }
        return { firstUnreadIndex: -1, hasUnreadAbove: false };
    }
    const boundaryIndex = messages.findIndex(message => message.getMessage().id === boundaryMessageId);
    const firstUnreadIndex = boundaryIndex > 0 ? boundaryIndex - 1 : -1;
    const hasUnreadAbove = boundaryIndex === -1 ? hasNext : firstUnreadIndex >= 0;
    return { firstUnreadIndex, hasUnreadAbove };
}
